import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Smartphone, Bell, Calendar, Music, Utensils, Palette,
  Laugh, Heart, Users, PartyPopper, Star, Check, Loader2, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const INTEREST_OPTIONS = [
  { id: 'music', label: 'Live Music', icon: Music },
  { id: 'food', label: 'Food & Drink', icon: Utensils },
  { id: 'art', label: 'Art & Culture', icon: Palette },
  { id: 'comedy', label: 'Comedy', icon: Laugh },
  { id: 'wellness', label: 'Wellness', icon: Heart },
  { id: 'networking', label: 'Networking', icon: Users },
  { id: 'nightlife', label: 'Nightlife', icon: PartyPopper },
];

const DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const { isAuthenticated, user, isGold, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/auth?redirect=/notifications/preferences'); return; }
    fetchPrefs();
  }, [isAuthenticated, authLoading]);

  const fetchPrefs = async () => {
    try {
      const res = await api.get('/engagement/preferences');
      setPrefs(res.data);
    } catch { toast.error('Failed to load preferences'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/engagement/preferences', prefs);
      toast.success('Preferences saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleInterest = (id) => {
    const current = prefs.interests || [];
    setPrefs({
      ...prefs,
      interests: current.includes(id) ? current.filter(i => i !== id) : [...current, id]
    });
  };

  if (loading || !prefs) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </ConsumerLayout>
    );
  }

  return (
    <ConsumerLayout hideFooter>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Notification Preferences</h1>
          <p className="text-muted-foreground mt-1">Control how and when you hear from EVENT★GOERS</p>
        </div>

        <div className="space-y-6">
          {/* Frequency & Channel */}
          <Card data-testid="prefs-frequency-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-indigo-600" /> Delivery Preferences</CardTitle>
              <CardDescription>Choose how often and how you receive updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Digest Frequency</Label>
                  <Select value={prefs.digest_frequency} onValueChange={(v) => setPrefs({...prefs, digest_frequency: v})}>
                    <SelectTrigger data-testid="prefs-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="none">None (opt out)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {prefs.digest_frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Preferred Day</Label>
                    <Select value={prefs.preferred_day} onValueChange={(v) => setPrefs({...prefs, preferred_day: v})}>
                      <SelectTrigger data-testid="prefs-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-xs text-muted-foreground">Receive digest and updates via email</p>
                    </div>
                  </div>
                  <Switch checked={prefs.channel_email} onCheckedChange={(v) => setPrefs({...prefs, channel_email: v})} data-testid="prefs-email-toggle" />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">SMS Text Messages</p>
                      <p className="text-xs text-muted-foreground">Get alerts and highlights via text</p>
                    </div>
                  </div>
                  <Switch checked={prefs.channel_sms} onCheckedChange={(v) => setPrefs({...prefs, channel_sms: v})} data-testid="prefs-sms-toggle" />
                </div>
                {prefs.channel_sms && (
                  <div className="ml-12 space-y-1.5">
                    <Label>Phone Number</Label>
                    <Input type="tel" placeholder="+1 (555) 000-0000" value={prefs.phone_number || ''} onChange={(e) => setPrefs({...prefs, phone_number: e.target.value})} data-testid="prefs-phone" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Interests */}
          <Card data-testid="prefs-interests-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Your Interests</CardTitle>
              <CardDescription>We'll personalize your digest based on what you love</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {INTEREST_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const selected = (prefs.interests || []).includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleInterest(opt.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                        selected ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-indigo-300'
                      }`}
                      data-testid={`interest-${opt.id}`}
                    >
                      <Icon className={`w-5 h-5 ${selected ? 'text-indigo-600' : 'text-muted-foreground'}`} />
                      <span className={`text-sm font-medium ${selected ? 'text-indigo-600' : 'text-foreground'}`}>{opt.label}</span>
                      {selected && <Check className="w-4 h-4 text-indigo-600 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Content Types */}
          <Card data-testid="prefs-content-card">
            <CardHeader>
              <CardTitle>What You'll Receive</CardTitle>
              <CardDescription>Toggle the types of content in your digest</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: 'upcoming_events', label: 'Upcoming Events', desc: 'Events happening in your area' },
                { key: 'new_experiences', label: 'New Experiences', desc: 'Latest bookable experiences' },
                { key: 'reservation_ideas', label: 'Reservation Ideas', desc: 'Top venues to reserve a table' },
                { key: 'partner_spotlights', label: 'Venue Spotlights', desc: 'Featured partner venues' },
                ...(isGold ? [{ key: 'gold_deals', label: 'Gold Member Deals', desc: 'Exclusive pricing and early access', isGold: true }] : []),
              ].map(item => (
                <div key={item.key} className={`flex items-center justify-between p-3 rounded-lg border ${item.isGold ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}>
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2">
                      {item.label}
                      {item.isGold && <Badge className="bg-[#D4AF37] text-white text-[10px]">GOLD</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch checked={prefs[item.key] !== false} onCheckedChange={(v) => setPrefs({...prefs, [item.key]: v})} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Save */}
          <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 font-semibold" data-testid="prefs-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Preferences</>}
          </Button>

          {/* Unsubscribe */}
          <p className="text-xs text-center text-muted-foreground">
            You'll always receive transactional emails (receipts, confirmations, reminders) regardless of these settings.
          </p>
        </div>
      </div>
    </ConsumerLayout>
  );
}
